/**
 * animals/animal-textures.js
 * Bộ tạo Vân phủ động (Procedural Canvas Texture) dành cho Động vật Voxel Trái Đất.
 */

import { shadeColor } from '../../core/color.js';
import { createCanvas } from '../../core/canvas-texture.js';

export { shadeColor };

function createRng(seed = 1) {
  let s = seed >>> 0 || 1;
  return function () {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 4294967296;
  };
}

/**
 * Sinh Texture Vân Động Vật Bằng HTML5 Canvas
 * @param {string} patternType - Tên kiểu vân hoa văn
 * @param {object} options - Màu nền, màu đốm, độ chi tiết, seed, isCleanFace...
 * @returns {HTMLCanvasElement}
 */
export function generateAnimalTexture(patternType = 'solid_fur', options = {}) {
  if (typeof document === 'undefined') {
    return null;
  }
  const size = options.size || 128;
  const canvas = createCanvas(size, size);
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  const baseColor = options.baseColor || '#e2e8f0';
  const patternColor = options.patternColor || '#1e293b';
  const accentColor = options.accentColor || '#ea580c';
  const seed = options.seed || 0;
  const isCleanFace = !!options.isCleanFace;

  // 1. Nền cơ sở với gradient chiều sâu
  const bgGrad = ctx.createLinearGradient(0, 0, size, size);
  bgGrad.addColorStop(0, baseColor);
  bgGrad.addColorStop(1, shadeColor(baseColor, -12));
  ctx.fillStyle = bgGrad;
  ctx.fillRect(0, 0, size, size);

  // 2. Vẽ hoa văn đặc thù theo từng kiểu giống loài
  if (isCleanFace) {
    // Mặt sạch phía trước (+Z): Không vẽ đốm lớn che mặt; chỉ cho phép 1 đốm nhỏ lệch bên tai
    if (patternType === 'cow_spots') {
      ctx.fillStyle = patternColor;
      ctx.beginPath();
      ctx.ellipse(size * 0.22, size * 0.18, size * 0.08, size * 0.06, 0.2, 0, Math.PI * 2);
      ctx.fill();
    } else if (patternType === 'calico') {
      ctx.fillStyle = patternColor;
      ctx.beginPath();
      ctx.ellipse(size * 0.22, size * 0.16, size * 0.07, size * 0.06, -0.2, 0, Math.PI * 2);
      ctx.fill();
    }
  } else if (patternType === 'cow_spots') {
    ctx.fillStyle = patternColor;
    if (seed === 0) {
      // Đốm loang bò sữa hữu cơ ngẫu nhiên mặc định (bảo toàn snapshot)
      const spots = [
        { x: size * 0.25, y: size * 0.28, rx: size * 0.2, ry: size * 0.16, rot: 0.2 },
        { x: size * 0.72, y: size * 0.35, rx: size * 0.18, ry: size * 0.22, rot: -0.3 },
        { x: size * 0.4, y: size * 0.72, rx: size * 0.24, ry: size * 0.18, rot: 0.1 },
        { x: size * 0.85, y: size * 0.8, rx: size * 0.15, ry: size * 0.14, rot: 0.4 }
      ];
      spots.forEach((sp) => {
        ctx.beginPath();
        ctx.ellipse(sp.x, sp.y, sp.rx, sp.ry, sp.rot, 0, Math.PI * 2);
        ctx.fill();
      });
    } else {
      const rng = createRng(seed * 77 + 13);
      const spots = [
        {
          x: size * (0.2 + rng() * 0.15),
          y: size * (0.2 + rng() * 0.25),
          rx: size * (0.16 + rng() * 0.06),
          ry: size * (0.14 + rng() * 0.06),
          rot: (rng() - 0.5) * 0.8
        },
        {
          x: size * (0.65 + rng() * 0.15),
          y: size * (0.3 + rng() * 0.2),
          rx: size * (0.15 + rng() * 0.06),
          ry: size * (0.16 + rng() * 0.06),
          rot: (rng() - 0.5) * 0.8
        },
        {
          x: size * (0.35 + rng() * 0.15),
          y: size * (0.65 + rng() * 0.2),
          rx: size * (0.18 + rng() * 0.06),
          ry: size * (0.14 + rng() * 0.06),
          rot: (rng() - 0.5) * 0.8
        }
      ];
      spots.forEach((sp) => {
        ctx.beginPath();
        ctx.ellipse(sp.x, sp.y, sp.rx, sp.ry, sp.rot, 0, Math.PI * 2);
        ctx.fill();
      });
    }
  } else if (patternType === 'dalmatian_spots') {
    ctx.fillStyle = patternColor;
    if (seed === 0) {
      // Đốm chấm tròn chó đốm rải đều tự nhiên
      const dots = [
        { x: 0.18, y: 0.22, r: 0.06 },
        { x: 0.42, y: 0.15, r: 0.05 },
        { x: 0.75, y: 0.26, r: 0.07 },
        { x: 0.28, y: 0.52, r: 0.08 },
        { x: 0.62, y: 0.48, r: 0.06 },
        { x: 0.86, y: 0.62, r: 0.05 },
        { x: 0.2, y: 0.82, r: 0.06 },
        { x: 0.5, y: 0.78, r: 0.07 },
        { x: 0.78, y: 0.88, r: 0.05 }
      ];
      dots.forEach((d) => {
        ctx.beginPath();
        ctx.arc(size * d.x, size * d.y, size * d.r, 0, Math.PI * 2);
        ctx.fill();
      });
    } else {
      const rng = createRng(seed * 53 + 7);
      const count = 7 + Math.floor(rng() * 4);
      for (let i = 0; i < count; i++) {
        const x = size * (0.15 + rng() * 0.7);
        const y = size * (0.15 + rng() * 0.7);
        const r = size * (0.05 + rng() * 0.03);
        ctx.beginPath();
        ctx.arc(x, y, r, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  } else if (patternType === 'tabby_stripes') {
    ctx.fillStyle = patternColor;
    const stripeCount = 6;
    const rng = seed > 0 ? createRng(seed * 31 + 5) : () => 0.5;
    for (let i = 0; i < stripeCount; i++) {
      const y = (size / stripeCount) * i + size * (seed > 0 ? 0.06 + rng() * 0.04 : 0.08);
      ctx.beginPath();
      ctx.moveTo(size * 0.05, y);
      ctx.quadraticCurveTo(size * 0.45, y - size * 0.06, size * 0.95, y + size * 0.02);
      ctx.lineTo(size * 0.95, y + size * 0.07);
      ctx.quadraticCurveTo(size * 0.45, y + size * 0.01, size * 0.05, y + size * 0.07);
      ctx.closePath();
      ctx.fill();
    }
  } else if (patternType === 'calico') {
    if (seed === 0) {
      // Mèo tam thể: Đốm đen xen kẽ đốm vàng cam trên nền trắng
      ctx.fillStyle = patternColor; // Màu đen
      ctx.beginPath();
      ctx.ellipse(size * 0.3, size * 0.35, size * 0.22, size * 0.18, -0.2, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = accentColor; // Màu cam vàng
      ctx.beginPath();
      ctx.ellipse(size * 0.75, size * 0.65, size * 0.24, size * 0.2, 0.3, 0, Math.PI * 2);
      ctx.fill();
    } else {
      const rng = createRng(seed * 41 + 11);
      ctx.fillStyle = patternColor;
      ctx.beginPath();
      ctx.ellipse(
        size * (0.3 + rng() * 0.1),
        size * (0.35 + rng() * 0.1),
        size * 0.2,
        size * 0.16,
        -0.2,
        0,
        Math.PI * 2
      );
      ctx.fill();

      ctx.fillStyle = accentColor;
      ctx.beginPath();
      ctx.ellipse(
        size * (0.7 + rng() * 0.1),
        size * (0.6 + rng() * 0.1),
        size * 0.22,
        size * 0.18,
        0.3,
        0,
        Math.PI * 2
      );
      ctx.fill();
    }
  } else if (patternType === 'wool_fluff') {
    // Vân len cừu: Vòng xoắn xốp nhẹ tạo cảm giác phồng
    ctx.strokeStyle = shadeColor(baseColor, -18);
    ctx.lineWidth = 2.5;
    const step = size / 4;
    for (let x = step * 0.5; x < size; x += step) {
      for (let y = step * 0.5; y < size; y += step) {
        ctx.beginPath();
        ctx.arc(x, y, step * 0.35, 0, Math.PI * 1.6);
        ctx.stroke();
      }
    }
  } else if (patternType === 'feather_layer') {
    // Vân vảy lông vũ xếp lớp cho gia cầm
    ctx.strokeStyle = shadeColor(baseColor, -15);
    ctx.lineWidth = 2;
    const rows = 5;
    for (let r = 0; r < rows; r++) {
      const y = (size / rows) * r + size * 0.1;
      const cols = 4;
      for (let c = 0; c < cols; c++) {
        const offset = r % 2 === 0 ? 0 : (size / cols) * 0.5;
        const x = (size / cols) * c + offset;
        ctx.beginPath();
        ctx.arc(x + (size / cols) * 0.5, y, (size / cols) * 0.4, 0, Math.PI);
        ctx.stroke();
      }
    }
  } else if (patternType === 'pig_skin') {
    // Da lợn hồng hào: Điểm ửng hồng phớt nhẹ ở trung tâm
    const blush = ctx.createRadialGradient(size * 0.5, size * 0.5, 0, size * 0.5, size * 0.5, size * 0.45);
    blush.addColorStop(0, shadeColor(baseColor, 10));
    blush.addColorStop(1, baseColor);
    ctx.fillStyle = blush;
    ctx.fillRect(0, 0, size, size);
  } else if (patternType === 'mallard') {
    // Vịt uyên ương: Gradient xanh ngọc bích ánh kim
    const sheen = ctx.createLinearGradient(0, 0, size, size);
    sheen.addColorStop(0, '#047857');
    sheen.addColorStop(0.5, '#0d9488');
    sheen.addColorStop(1, '#065f46');
    ctx.fillStyle = sheen;
    ctx.fillRect(0, 0, size, size);

    ctx.strokeStyle = 'rgba(255, 255, 255, 0.25)';
    ctx.lineWidth = 2.5;
    ctx.beginPath();
    ctx.moveTo(size * 0.1, size * 0.5);
    ctx.lineTo(size * 0.9, size * 0.5);
    ctx.stroke();
  }

  // 3. Đường viền bóng đổ cạnh tinh tế chuẩn phong cách Voxel
  ctx.strokeStyle = 'rgba(0, 0, 0, 0.08)';
  ctx.lineWidth = 2;
  ctx.strokeRect(1, 1, size - 2, size - 2);

  return canvas;
}
